import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { store } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';

const getDefaultWorkspaceName = (firstName) => {
  const trimmed = String(firstName || '').trim();
  return trimmed ? `${trimmed}'s Workspace` : 'My Workspace';
};

const normalizeUserWorkspace = (userId, user = {}) => {
  // Never fabricate a workspace id here - the server-side authorization
  // middleware provisions the default workspace for users without one.
  const selectedWorkspaceId = user.selectedWorkspaceId || user.defaultWorkspaceId || null;
  const defaultWorkspaceId = user.defaultWorkspaceId || selectedWorkspaceId || null;
  const workspaceIds = Array.isArray(user.workspaceIds)
    ? selectedWorkspaceId && !user.workspaceIds.includes(selectedWorkspaceId)
      ? [...user.workspaceIds, selectedWorkspaceId]
      : user.workspaceIds
    : selectedWorkspaceId
      ? [selectedWorkspaceId]
      : [];

  return {
    ...user,
    role: user.role || 'user',
    workspaceIds,
    selectedWorkspaceId,
    defaultWorkspaceId,
    defaultWorkspaceName: user.defaultWorkspaceName || getDefaultWorkspaceName(user.firstName),
    id: user.id || userId
  };
};

export const getSignedInUser = async (userId) => {
  //Get User Profile from store
  const userRef = doc(store, 'users', userId);
  const userSnap = await getDoc(userRef);
  const user = userSnap.data() || {};

  const normalizedUser = normalizeUserWorkspace(userId, user);

  if (
    normalizedUser.selectedWorkspaceId &&
    (user.selectedWorkspaceId !== normalizedUser.selectedWorkspaceId ||
      user.defaultWorkspaceId !== normalizedUser.defaultWorkspaceId ||
      JSON.stringify(user.workspaceIds || []) !== JSON.stringify(normalizedUser.workspaceIds))
  ) {
    await setDoc(
      userRef,
      {
        workspaceIds: normalizedUser.workspaceIds,
        selectedWorkspaceId: normalizedUser.selectedWorkspaceId,
        defaultWorkspaceId: normalizedUser.defaultWorkspaceId,
        defaultWorkspaceName: normalizedUser.defaultWorkspaceName,
        updatedAt: new Date()
      },
      { merge: true }
    );
  }

  return normalizedUser;
};

//Create user profile when signed up, the auth user will return a ID that needs to be used for the user id.
export const createUserProfile = async (userId, user) => {
  const userRef = doc(store, 'users', userId);
  const selectedWorkspaceId = uuidv4();

  //Include timestamps
  user.createdAt = new Date();
  user.updatedAt = new Date();
  user.workspaceIds = [selectedWorkspaceId];
  user.selectedWorkspaceId = selectedWorkspaceId;
  user.defaultWorkspaceId = selectedWorkspaceId;
  user.defaultWorkspaceName = getDefaultWorkspaceName(user.firstName);

  await setDoc(userRef, user);

  try {
    const workspaceRef = doc(store, 'workspaces', selectedWorkspaceId);
    await setDoc(workspaceRef, {
      name: getDefaultWorkspaceName(user.firstName),
      ownerId: userId,
      members: [{ id: userId, role: 'Admin' }],
      memberIds: [userId],
      createdAt: new Date(),
      updatedAt: new Date()
    });
  } catch (error) {
    // Server-side authorization middleware will provision missing default workspace metadata.
    console.warn('Unable to create workspace document from client profile setup.', error);
  }
};

export const getUserByEmail = async (email) => {
  try {
    const usersCollection = collection(store, 'users');
    const q = query(usersCollection, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const user = userDoc.data();
      return user;
    } else {
      console.log('No user found with the provided email.');
      return null;
    }
  } catch (error) {
    console.error('Error fetching user by email: ', error);
    throw error;
  }
};
